import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { merchantPermissionsAPI } from '../services/merchantAPI';
import { useMerchantAuth } from './MerchantAuthContext';

const MerchantPermissionContext = createContext();

export const useMerchantPermissions = () => {
	const context = useContext(MerchantPermissionContext);
	if (!context) {
		throw new Error('useMerchantPermissions must be used within a MerchantPermissionProvider');
	}
	return context;
};

export const MerchantPermissionProvider = ({ children }) => {
	const { merchant, token } = useMerchantAuth();
	const location = useLocation();
	const [modules, setModules] = useState([]);
	const [permissions, setPermissions] = useState([]);
	const [userPermissions, setUserPermissions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [initialized, setInitialized] = useState(false);

	// Track latest fetch to ignore stale results
	const lastFetchKeyRef = useRef(null);

	// Fetch merchant permissions and modules
	const fetchPermissions = useCallback(async () => {
		if (!token || !merchant) {
			setModules([]);
			setPermissions([]);
			setUserPermissions([]);
			setError(null);
			setLoading(false);
			setInitialized(true);
			return;
		}

		const fetchKey = `${token}:${merchant?.id || merchant?.email}`;
		lastFetchKeyRef.current = fetchKey;

		try {
			setLoading(true);
			setError(null);

			// Fetch modules, permissions, and user-specific permissions in parallel
			const [modulesResponse, permissionsResponse, userPermissionsResponse] = await Promise.all([
				merchantPermissionsAPI.getModules(),
				merchantPermissionsAPI.getPermissions(),
				merchantPermissionsAPI.getUserPermissions()
			]);

			// Ignore if auth state changed during fetch
			if (lastFetchKeyRef.current !== fetchKey) return;

			setModules(modulesResponse.data || []);
			setPermissions(permissionsResponse.data || []);
			setUserPermissions(userPermissionsResponse.data?.user_permissions || []);
			
			// console.log('DEBUG: Fetched permissions:', {
			// 	modules: modulesResponse.data?.length || 0,
			// 	permissions: permissionsResponse.data?.length || 0,
			// 	userPermissions: userPermissionsResponse.data?.user_permissions?.length || 0,
			// 	userPermissionsData: userPermissionsResponse.data
			// });
		} catch (error) {
			if (lastFetchKeyRef.current === fetchKey) {
				console.error('DEBUG: Error fetching permissions:', error);
				setError(error.message || 'Failed to load permissions');
				setModules([]);
				setPermissions([]);
				setUserPermissions([]);
			}
		} finally {
			if (lastFetchKeyRef.current === fetchKey) {
				// Add a small delay to prevent flickering during page refreshes
				setTimeout(() => {
					setLoading(false);
					setInitialized(true);
				}, 100);
			}
		}
	// Only depend on stable identifiers to avoid re-fetch on merchant object changes
	}, [token, merchant?.id]);

	// Load permissions when merchant auth changes
	useEffect(() => {
		fetchPermissions();
	}, [fetchPermissions]);

	// Re-fetch on every route change so DB updates are reflected without logout/login
	useEffect(() => {
		if (initialized && token && merchant) {
			fetchPermissions();
		}
	}, [location.pathname]);

	// Check if user has permission for a specific module and action
	const hasPermission = useCallback((moduleName, actionName) => {
		if (!permissions || permissions.length === 0) return false;

		const permission = permissions.find(perm =>
			perm.module === moduleName && perm.action === actionName
		);

		if (permission) {
			console.log(`DEBUG: Permission check for ${moduleName}.${actionName}:`, {
				hasPermission: true,
				source: permission.source,
				permission_type: permission.permission_type
			});
		} else {
			console.log(`DEBUG: Permission check for ${moduleName}.${actionName}:`, {
				hasPermission: false,
				availablePermissions: permissions.map(p => `${p.module}.${p.action}`)
			});
		}

		return !!permission;
	}, [permissions]);

	// Check if user has access to a module (has any permission for that module)
	const hasModuleAccess = useCallback((moduleName) => {
		if (!permissions || permissions.length === 0) return false;
		// Only check for 'view' permission to determine menu visibility
		// Other actions can be removed without hiding the menu
		return permissions.some(perm => perm.module === moduleName && perm.action === 'view');
	}, [permissions]);

	// Check if user has any permission for a module (for other functionality)
	const hasAnyModulePermission = useCallback((moduleName) => {
		if (!permissions || permissions.length === 0) return false;
		return permissions.some(perm => perm.module === moduleName);
	}, [permissions]);

	// Get accessible modules based on permissions
	const getAccessibleModules = useCallback(() => {
		if (!modules || modules.length === 0) return [];
		return modules.filter(module => hasModuleAccess(module.module_name));
	}, [modules, hasModuleAccess]);

	// Get actions for a specific module that user has access to
	const getModuleActions = useCallback((moduleName) => {
		if (!permissions || permissions.length === 0) return [];
		return permissions.filter(perm => perm.module === moduleName).map(perm => perm.action);
	}, [permissions]);

	// Get user-specific permission overrides
	const getUserPermissionOverrides = useCallback(() => {
		return userPermissions;
	}, [userPermissions]);

	// Expose a stable value to avoid re-renders
	const value = useMemo(() => ({
		modules,
		permissions,
		userPermissions,
		loading,
		initialized,
		error,
		hasPermission,
		hasModuleAccess,
		hasAnyModulePermission,
		getAccessibleModules,
		getModuleActions,
		getUserPermissionOverrides,
		refreshPermissions: fetchPermissions
	}), [modules, permissions, userPermissions, loading, initialized, error, hasPermission, hasModuleAccess, hasAnyModulePermission, getAccessibleModules, getModuleActions, getUserPermissionOverrides, fetchPermissions]);

	return (
		<MerchantPermissionContext.Provider value={value}>
			{children}
		</MerchantPermissionContext.Provider>
	);
}; 